import { useEffect, useRef, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { GameTitle } from '../../../components/GameTitle'
import { readError } from '../../../lib/errors'
import {
  enterOnlineQueue, leaveOnlineQueue,
} from '../../colorful/api/online'
import { fetchMyMatchmadeIETRoom } from '../api/online'
import { useMatchmakingTimeout } from '../../../lib/useMatchmakingTimeout'
import { useT } from '../../../i18n'

interface Props {
  onBack: () => void
  onMatched: (roomId: string) => void
}

const POLL_INTERVAL_MS = 3000
const TARGET_PLAYERS = 4

/**
 * IET online-match waiting screen. 1:1 mirror of
 * ColorfulOnlineMatchScreen — enters the 'ideatthat' queue, then
 * re-enters it every poll tick (heartbeat + queue count + the
 * server's open-room scan, so a public party room that gains a free
 * seat AFTER we queued still pulls us in) and polls the caller's
 * matchmade room to catch a 4-way match fired by someone else's
 * call. Routes into the same LiveRoomScreen once a room id is
 * available.
 */
export function IETOnlineMatchScreen({ onBack, onMatched }: Props) {
  const t = useT()
  const [queuedCount, setQueuedCount] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<'entering' | 'waiting' | 'matched' | 'timeout'>('entering')
  // Newest matchmade room from BEFORE this search began.
  // my_matchmade_iet_room also returns unfinished games from earlier
  // sessions; without this baseline the first poll tick would "match"
  // straight into one of those instead of a fresh game.
  const staleRoomIdRef = useRef<string | null>(null)

  // Don't let a player sit in the queue forever.
  useMatchmakingTimeout('ideatthat', status === 'waiting', () => setStatus('timeout'))

  useEffect(() => {
    let cancelled = false
    async function start() {
      setError(null)
      try {
        try {
          const existing = await fetchMyMatchmadeIETRoom()
          staleRoomIdRef.current = existing?.id ?? null
        } catch { /* non-fatal — worst case an old room is treated as the match */ }
        if (cancelled) return
        const result = await enterOnlineQueue('ideatthat')
        if (cancelled) return
        setQueuedCount(result.queued_count)
        if (result.matched && result.room_id) {
          setStatus('matched')
          onMatched(result.room_id)
          return
        }
        setStatus('waiting')
      } catch (e) {
        if (!cancelled) {
          setError(readError(e))
          setStatus('waiting')
        }
      }
    }
    start()
    return () => {
      cancelled = true
      // Always leave, matched or not: the per-tick re-entry can re-add
      // our queue row right after a rival's call formed our match, so
      // "matched" no longer implies the row is already gone.
      void leaveOnlineQueue('ideatthat').catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Poll: first check for a room a rival's call may have formed for
  // us, then re-enter the queue (heartbeat + count + open-room scan).
  useEffect(() => {
    if (status !== 'waiting') return
    let cancelled = false
    const tick = async () => {
      try {
        const room = await fetchMyMatchmadeIETRoom()
        if (cancelled) return
        if (room?.id && room.id !== staleRoomIdRef.current) {
          setStatus('matched')
          onMatched(room.id)
          return
        }
        const s = await enterOnlineQueue('ideatthat')
        if (cancelled) return
        setQueuedCount(s.queued_count)
        if (s.matched && s.room_id) {
          setStatus('matched')
          onMatched(s.room_id)
        }
      } catch (e) {
        if (!cancelled) setError(readError(e))
      }
    }
    const id = setInterval(tick, POLL_INTERVAL_MS)
    tick()
    return () => { cancelled = true; clearInterval(id) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  async function cancel() {
    try { await leaveOnlineQueue('ideatthat') } catch {}
    onBack()
  }

  return (
    <div className="screen">
      <PageHeader title={t('Online match')} onBack={cancel} />

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 gap-6">
        <GameTitle game="ideatthat" size="lg" />

        <div className="text-center">
          <div className="w-24 h-24 mx-auto rounded-full bg-brand-elevated
                          flex items-center justify-center animate-pulse-glow mb-6">
            <span className="text-4xl">🌐</span>
          </div>
          <h2 className="font-display text-2xl font-black text-brand-text mb-1">
            {status === 'timeout' ? t('Matchmaking timed out') : t('Finding a match…')}
          </h2>
          <p className="text-brand-muted text-sm">
            {status === 'timeout'
              ? t('No match came together this time. Head back and give it another go in a bit.')
              : t("You'll be slotted into the next open game of {n}.", { n: TARGET_PLAYERS })}
          </p>
        </div>

        {status !== 'timeout' && (
          <div className="w-full max-w-xs">
            <div className="flex items-center justify-center gap-2 mb-3">
              {Array.from({ length: TARGET_PLAYERS }).map((_, i) => {
                const filled = i < Math.min(queuedCount, TARGET_PLAYERS)
                return (
                  <div
                    key={i}
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all
                               ${filled
                                 ? 'bg-brand-text text-white border-brand-text'
                                 : 'bg-white text-brand-muted border-brand-border'}`}
                  >
                    {filled ? '👤' : '…'}
                  </div>
                )
              })}
            </div>
            <p className="text-center text-sm text-brand-muted">
              {t('{n} of {total} players queued', { n: queuedCount, total: TARGET_PLAYERS })}
            </p>
          </div>
        )}

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 max-w-sm w-full">
            <p className="text-sm text-brand-danger text-center">{error}</p>
          </div>
        )}

        <div className="text-center text-xs text-brand-muted max-w-xs px-3">{t("Online games pause between turns. Close the app and come back when it's your turn. If someone drops, any player can skip their seat to keep the game moving.")}</div>
      </div>

      <div className="floating-actions">
        <button onClick={cancel} className="btn-outline">
          {status === 'timeout' ? t('Back to menu') : t('Cancel matchmaking')}
        </button>
      </div>
    </div>
  )
}
